"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { BadgeCheck, BookOpenText, Check, Copy, Keyboard, Loader2, Pill } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CertifiedMedicine } from "@/entities/certified-medicine";
import type { MedicineDetails, MedicineIcdCode, MedicineVariation, MedicineVendorInfo } from "@/entities/medicine/api/medicines";
import { translateContainerType, translateDosageForm, translateProductType, translateStrength } from "@/entities/medicine/lib/medicine-label";
import { DosageFormIcon } from "@/entities/medicine/ui/dosage-form-icon";
import { MedicineAnaloguesBrowser } from "@/entities/medicine/ui/medicine-analogues-browser";
import { MedicineCertificatesList } from "@/entities/medicine/ui/medicine-certificates-list";
import { splitReferentPrices, type ReferentPriceEntry } from "@/entities/referent-price";
import { useReferentPrice } from "@/entities/referent-price/model/use-referent-price";
import { formatIssueDate } from "@/shared/lib/format/dates";
import { toIntlLocale } from "@/shared/lib/format/locale";
import { formatUzsValue } from "@/shared/lib/format/numbers";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/badge";
import { filterToggleVariants } from "@/shared/ui/filter-toggle";
import { Skeleton } from "@/shared/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
type MedicineDetailsCardProps = {
  details: MedicineDetails | null;
  loading: boolean;
  error: string | null;
  icdCodes?: MedicineIcdCode[] | null;
  variations?: MedicineVariation[] | null;
  variationsLoading?: boolean;
  variationsError?: string | null;
  analogues?: MedicineDetails[] | null;
  analoguesLoading?: boolean;
  analoguesError?: string | null;
  certificates?: CertifiedMedicine[] | null;
  certificatesLoading?: boolean;
  certificatesError?: string | null;
  currentMedicineId?: string | null;
  requestedTab?: "analogues" | null;
  onRequestedTabChange?: (tab: "analogues" | null) => void;
  /**
   * If provided, variant rows render as buttons that invoke this callback
   * instead of navigating to the medicine detail route.
   */
  onSelectVariation?: (medicineId: string) => void;
  onSelectAnalogue?: (medicineId: string) => void;
};
type DetailFieldItem = {
  key: string;
  label: string;
  value: string;
};
type DetailGroupTabItem = {
  value: string;
  label: string;
  fields: DetailFieldItem[];
};
type StaticTabItem = {
  type: "analogues" | "icdCodes" | "variations" | "certificates";
  value: string;
  label: string;
};
type DetailsTabItem = {
  type: "details";
  value: "details";
  label: string;
};
type MedicineDetailsTabItem = DetailsTabItem | StaticTabItem;
function sortByAttributes(a: MedicineVariation, b: MedicineVariation): number {
  const dosageFormComparison = String(a.dosage_form ?? "").localeCompare(String(b.dosage_form ?? ""), undefined, {
    numeric: true,
    sensitivity: "base"
  });
  if (dosageFormComparison !== 0) return dosageFormComparison;
  const attrs: Array<keyof MedicineVariation> = ["strength", "volume", "pack"];
  for (const attr of attrs) {
    const left = String(a[attr] ?? "");
    const right = String(b[attr] ?? "");
    const comparison = left.localeCompare(right, undefined, {
      numeric: true
    });
    if (comparison !== 0) return comparison;
  }
  return 0;
}

/**
 * Fixes PDF-extraction word-wrap artefacts without destroying real structure.
 *
 * Rules:
 *  - A lone `-` on its own line is a bullet separator → `• `
 *  - Lines with ≤ 3 words are word-wrap fragments: they get merged into the
 *    next long line (or flushed on an empty line / end of text).
 *  - Lines with > 3 words are kept as-is (they carry real content).
 *  - Empty lines (paragraph / bullet separators) are preserved.
 */
function normalizeText(text: string): string {
  const prepared = text.replace(/\r\n/g, "\n").replace(/\n-\n/g, "\n\n• ").replace(/^-\n/, "• ");
  const lines = prepared.split("\n");
  const output: string[] = [];
  let buffer = "";
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (buffer) output.push(buffer);
      output.push("");
      buffer = "";
      continue;
    }
    const wordCount = line.split(/\s+/).filter(Boolean).length;
    if (wordCount <= 3) {
      // Short line — accumulate; will be prepended to the next long line
      buffer = buffer ? `${buffer} ${line}` : line;
    } else {
      // Long line — absorb any buffered short fragment, then emit
      output.push(buffer ? `${buffer} ${line}` : line);
      buffer = "";
    }
  }
  if (buffer) output.push(buffer);
  return output.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
function humanizeShelfLife(months: number, t: ReturnType<typeof useTranslation>["t"]): string {
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(t("medicines.details.shelfLifeYear", {
    count: years
  }));
  if (remainingMonths > 0) parts.push(t("medicines.details.shelfLifeMonth", {
    count: remainingMonths
  }));
  if (parts.length === 0) parts.push(t("medicines.details.shelfLifeMonth", {
    count: 0
  }));
  return parts.join(" ");
}
function normalizeDetailValue(value: string | number | boolean | null | undefined): string | null {
  if (value == null) return null;
  if (typeof value === "boolean") return value ? "true" : "false";
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}
function detailField(key: string, label: string, value: string | number | boolean | null | undefined): DetailFieldItem | null {
  const normalized = normalizeDetailValue(value);
  return normalized ? {
    key,
    label,
    value: normalized
  } : null;
}
function compactSourceName(source: string | null | undefined): string | null {
  const normalized = normalizeDetailValue(source);
  if (!normalized) return null;
  if (normalized.toLowerCase() === "vidal") return "Vidal";
  if (normalized.toLowerCase() === "rls") return "RLS";
  return normalized;
}
function getCanonicalSourceUrl(details: MedicineDetails): string | null {
  const metadata = details.source_metadata;
  const canonical = metadata && typeof metadata === "object" && typeof metadata.canonical_url === "string" ? metadata.canonical_url : null;
  return normalizeDetailValue(details.source_url) ?? normalizeDetailValue(canonical);
}
function joinFieldValues(values: Array<string | null | undefined>): string | null {
  const parts = values.map(value => normalizeDetailValue(value)).filter((value): value is string => Boolean(value));
  return parts.length > 0 ? parts.join(" · ") : null;
}
function compactIcd10Codes(codes: MedicineDetails["icd10_codes"]): string | null {
  if (!Array.isArray(codes) || codes.length === 0) return null;
  const values = codes.map(item => {
    const code = normalizeDetailValue(item?.code);
    if (!code) return null;
    const label = normalizeDetailValue(item?.label);
    return label ? `${code} ${label}` : code;
  }).filter((value): value is string => Boolean(value));
  return values.length > 0 ? values.join(", ") : null;
}
function compactMappedIcdCodes(codes: MedicineIcdCode[] | null | undefined): string | null {
  if (!Array.isArray(codes) || codes.length === 0) return null;
  const values = codes.map(item => {
    const code = normalizeDetailValue(item?.code);
    if (!code) return null;
    const description = normalizeDetailValue(item?.description);
    return description ? `${code} - ${description}` : code;
  }).filter((value): value is string => Boolean(value));
  return values.length > 0 ? values.join("\n") : null;
}
export function MedicineDetailsCard({
  details,
  loading,
  error,
  icdCodes,
  variations,
  variationsLoading: _variationsLoading = false,
  variationsError = null,
  analogues,
  analoguesLoading = false,
  analoguesError = null,
  certificates = null,
  certificatesLoading = false,
  certificatesError = null,
  currentMedicineId = null,
  requestedTab = null,
  onRequestedTabChange,
  onSelectVariation,
  onSelectAnalogue
}: MedicineDetailsCardProps) {
  const {
    t
  } = useTranslation();
  const ANALOGUES_TAB = "analogues" as const;
  const referentQuery = useReferentPrice(details?.id ?? null);
  const filteredVariations = React.useMemo(() => variations ? variations.filter(item => String(item.id) !== currentMedicineId) : [], [variations, currentMedicineId]);
  const hasVariations = filteredVariations.length > 0;
  const showVariationsTab = variationsError != null || hasVariations;
  const sortedVariations = React.useMemo(() => hasVariations ? [...filteredVariations].sort(sortByAttributes) : [], [filteredVariations, hasVariations]);
  if (loading) {
    return <section className="bg-card ring-foreground/8 flex items-center gap-3 rounded-xl border-none p-6 shadow-xs ring-1">
        <Loader2 className="text-muted-foreground size-4 animate-spin" />
        <Skeleton className="h-4 w-40" />
      </section>;
  }
  if (error) {
    return <section className="bg-card ring-destructive/40 rounded-xl border-none p-6 shadow-xs ring-1">
        <p className="text-destructive text-sm">{error}</p>
      </section>;
  }
  if (!details) return null;
  const displayName = details.trade_name || details.name;
  const metaChips: Array<{
    label: string;
  }> = [];
  if (details.dosage_form) metaChips.push({
    label: translateDosageForm(details.dosage_form) ?? details.dosage_form
  });
  if (details.strength) metaChips.push({
    label: details.strength
  });
  if (details.volume) metaChips.push({
    label: details.volume
  });
  if (details.container_type) metaChips.push({
    label: translateContainerType(details.container_type) ?? details.container_type
  });
  if (details.pack != null) metaChips.push({
    label: `№${details.pack}`
  });
  if (details.atc_code) metaChips.push({
    label: t("medicines.details.atcLabel", {
      code: details.atc_code
    })
  });
  if (details.product_type && !isGenericMedicineProductType(details.product_type)) metaChips.push({
    label: translateProductType(details.product_type) ?? details.product_type
  });
  if (details.shelf_life_months != null) metaChips.push({
    label: `${t("medicines.details.shelfLife")}: ${humanizeShelfLife(details.shelf_life_months, t)}`
  });
  const sourceName = compactSourceName(details.source);
  const sourceUrl = getCanonicalSourceUrl(details);
  const sourceAriaLabel = sourceName ? t("medicines.details.openSourceNamed", {
    source: sourceName
  }) : t("medicines.details.openSource", {
    defaultValue: "Open source"
  });
  const sourceTooltip = [sourceName ? t("medicines.details.sourceNamed", {
    source: sourceName
  }) : t("medicines.details.source"), details.source_updated_at ? t("medicines.details.sourceUpdated", {
    date: details.source_updated_at
  }) : null].filter(Boolean).join(" · ");
  const activeSubstance = joinFieldValues([details.active_substance_ru, details.active_substance_en]);
  const mappedIcdPurpose = compactMappedIcdCodes(icdCodes);
  const detailTabItems: DetailGroupTabItem[] = [{
    value: "summary",
    label: t("medicines.details.groups.summary"),
    fields: [detailField("displayName", t("medicines.details.displayName"), details.display_name), detailField("tradeNameLatin", t("medicines.details.tradeNameLatin"), details.trade_name_latin), detailField("dosageFormText", t("medicines.details.dosageFormText"), details.dosage_form_text), detailField("activeSubstance", t("medicines.details.activeSubstance"), activeSubstance), detailField("clinicalPharmacologicalGroup", t("medicines.details.clinicalPharmacologicalGroup"), details.clinical_pharmacological_group), detailField("pharmacotherapeuticGroup", t("medicines.details.pharmacotherapeuticGroup"), details.pharmacotherapeutic_group), detailField("manufacturerName", t("medicines.details.manufacturerName"), details.manufacturer_name), detailField("contactOrganizationName", t("medicines.details.contactOrganizationName"), details.contact_organization_name), detailField("icd10Codes", t("medicines.details.icd10Codes"), compactIcd10Codes(details.icd10_codes))].filter((field): field is DetailFieldItem => Boolean(field))
  }, {
    value: "use",
    label: t("medicines.details.groups.use"),
    fields: [detailField("usedFor", t("medicines.details.usedFor"), mappedIcdPurpose), detailField("indications", t("medicines.details.indications"), details.indications), detailField("dosageInstructions", t("medicines.details.dosageInstructions"), details.dosage_instructions)].filter((field_0): field_0 is DetailFieldItem => Boolean(field_0))
  }, {
    value: "safety",
    label: t("medicines.details.groups.safety"),
    fields: [detailField("contraindications", t("medicines.details.contraindications"), details.contraindications), detailField("sideEffects", t("medicines.details.sideEffects"), details.side_effects), detailField("pregnancyLactation", t("medicines.details.pregnancyLactation"), details.pregnancy_lactation), detailField("hepaticImpairment", t("medicines.details.hepaticImpairment"), details.hepatic_impairment), detailField("renalImpairment", t("medicines.details.renalImpairment"), details.renal_impairment), detailField("pediatricUse", t("medicines.details.pediatricUse"), details.pediatric_use), detailField("geriatricUse", t("medicines.details.geriatricUse"), details.geriatric_use), detailField("specialInstructions", t("medicines.details.specialInstructions"), details.special_instructions), detailField("overdose", t("medicines.details.overdose"), details.overdose)].filter((field_1): field_1 is DetailFieldItem => Boolean(field_1))
  }, {
    value: "pharmacology",
    label: t("medicines.details.groups.pharmacology"),
    fields: [detailField("composition", t("medicines.details.composition"), details.composition), detailField("pharmacologicalAction", t("medicines.details.pharmacologicalAction"), details.pharmacological_action), detailField("pharmacokinetics", t("medicines.details.pharmacokinetics"), details.pharmacokinetics)].filter((field_2): field_2 is DetailFieldItem => Boolean(field_2))
  }, {
    value: "storage",
    label: t("medicines.details.groups.storage"),
    fields: [detailField("storageConditions", t("medicines.details.storageConditions"), details.storage_conditions), detailField("shelfLife", t("medicines.details.shelfLife"), details.shelf_life_months != null ? humanizeShelfLife(details.shelf_life_months, t) : null), detailField("pharmacyReleaseTerms", t("medicines.details.pharmacyReleaseTerms"), details.pharmacy_release_terms), detailField("drugInteractions", t("medicines.details.drugInteractions"), details.drug_interactions)].filter((field_3): field_3 is DetailFieldItem => Boolean(field_3))
  }].filter(group => group.fields.length > 0);
  const hasIcdCodes = icdCodes != null && icdCodes.length > 0;
  const hasCertificates = certificates != null && certificates.length > 0;
  const internationalName = details.international_name_ru ?? details.international_name;
  const hasAnalogueLookup = Boolean(details.atc_code || details.international_name);
  const detailTabValues = React.useMemo(() => detailTabItems.map(item_0 => item_0.value), [detailTabItems]);
  const detailFallbackTab = detailTabValues[0] ?? null;
  const hasDetailTabs = detailTabItems.length > 0;
  const tabItems = React.useMemo<MedicineDetailsTabItem[]>(() => {
    const items: MedicineDetailsTabItem[] = [];
    if (hasDetailTabs) {
      items.push({
        type: "details",
        value: "details",
        label: t("medicines.details.title")
      });
    }
    if (hasAnalogueLookup) {
      items.push({
        type: "analogues",
        value: ANALOGUES_TAB,
        label: t("medicines.analogues.title", {
          defaultValue: "Analogues"
        })
      });
    }
    if (hasIcdCodes) items.push({
      type: "icdCodes",
      value: "icdCodes",
      label: t("medicines.details.icdCodes")
    });
    if (showVariationsTab) {
      items.push({
        type: "variations",
        value: "variations",
        label: t("medicines.details.variations")
      });
    }
    if (hasCertificates) {
      items.push({
        type: "certificates",
        value: "certificates",
        label: t("medicines.details.certificates")
      });
    }
    return items;
  }, [ANALOGUES_TAB, hasAnalogueLookup, hasCertificates, hasDetailTabs, hasIcdCodes, showVariationsTab, t]);
  const tabValues = React.useMemo(() => tabItems.map(item_1 => item_1.value), [tabItems]);
  const fallbackTab = tabValues[0] ?? null;
  const [activeTab, setActiveTab] = React.useState<string | null>(() => requestedTab === ANALOGUES_TAB && hasAnalogueLookup ? ANALOGUES_TAB : fallbackTab);
  const [activeDetailTab, setActiveDetailTab] = React.useState<string | null>(() => detailFallbackTab);
  const tabsListRef = React.useRef<HTMLDivElement | null>(null);
  const detailTabsListRef = React.useRef<HTMLDivElement | null>(null);
  const lastAutoFocusedKeyRef = React.useRef<string | null>(null);
  const detailResetKey = currentMedicineId ?? details.id ?? details.medicine_id ?? "";
  React.useEffect(() => {
    setActiveDetailTab(detailFallbackTab);
  }, [detailFallbackTab, detailResetKey]);
  React.useEffect(() => {
    const hasActiveDetailTab = activeDetailTab != null && detailTabValues.includes(activeDetailTab);
    if (!hasActiveDetailTab && detailFallbackTab) {
      setActiveDetailTab(detailFallbackTab);
    }
  }, [activeDetailTab, detailFallbackTab, detailTabValues]);
  React.useEffect(() => {
    const hasActiveTab = activeTab != null && tabValues.includes(activeTab);
    if (requestedTab === ANALOGUES_TAB) {
      if (hasAnalogueLookup) {
        if (activeTab !== ANALOGUES_TAB) setActiveTab(ANALOGUES_TAB);
        return;
      }
      onRequestedTabChange?.(null);
      if (fallbackTab && activeTab !== fallbackTab) setActiveTab(fallbackTab);
      return;
    }
    if (!hasActiveTab && fallbackTab) {
      if (activeTab === ANALOGUES_TAB) onRequestedTabChange?.(null);
      setActiveTab(fallbackTab);
    }
  }, [ANALOGUES_TAB, activeTab, fallbackTab, hasAnalogueLookup, onRequestedTabChange, requestedTab, tabValues]);
  const handleTabChange = React.useCallback((nextValue: string) => {
    setActiveTab(nextValue);
    onRequestedTabChange?.(nextValue === ANALOGUES_TAB ? ANALOGUES_TAB : null);
  }, [ANALOGUES_TAB, onRequestedTabChange]);
  const handleDetailTabChange = React.useCallback((nextValue_0: string) => {
    setActiveDetailTab(nextValue_0);
  }, []);
  const cycleTab = React.useCallback((direction: 1 | -1) => {
    const tabsList = tabsListRef.current;
    if (!tabsList || tabValues.length <= 1) return;
    const triggers = Array.from(tabsList.querySelectorAll<HTMLElement>('[role="tab"]'));
    if (triggers.length === 0) return;
    const activeTrigger = tabsList.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    const currentIndex = activeTrigger ? triggers.indexOf(activeTrigger) : -1;
    const baseIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = (baseIndex + direction + tabValues.length) % tabValues.length;
    handleTabChange(tabValues[nextIndex]);
    triggers[nextIndex]?.focus();
  }, [handleTabChange, tabValues]);
  const cycleDetailTab = React.useCallback((direction_0: 1 | -1) => {
    const tabsList_0 = detailTabsListRef.current;
    if (!tabsList_0 || detailTabValues.length <= 1) return;
    const triggers_0 = Array.from(tabsList_0.querySelectorAll<HTMLElement>('[role="tab"]'));
    if (triggers_0.length === 0) return;
    const activeTrigger_0 = tabsList_0.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    const currentIndex_0 = activeTrigger_0 ? triggers_0.indexOf(activeTrigger_0) : -1;
    const baseIndex_0 = currentIndex_0 >= 0 ? currentIndex_0 : 0;
    const nextIndex_0 = (baseIndex_0 + direction_0 + detailTabValues.length) % detailTabValues.length;
    handleDetailTabChange(detailTabValues[nextIndex_0]);
    triggers_0[nextIndex_0]?.focus();
  }, [detailTabValues, handleDetailTabChange]);
  const handleTabsListKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab" || tabValues.length <= 1) return;
    const target = event.target as HTMLElement | null;
    const currentTrigger = target?.closest<HTMLElement>('[role="tab"]');
    if (!currentTrigger || !event.currentTarget.contains(currentTrigger)) return;
    event.preventDefault();
    cycleTab(event.shiftKey ? -1 : 1);
  }, [cycleTab, tabValues.length]);
  const handleDetailTabsListKeyDown = React.useCallback((event_0: React.KeyboardEvent<HTMLDivElement>) => {
    if (event_0.key !== "Tab" || detailTabValues.length <= 1) return;
    const target_0 = event_0.target as HTMLElement | null;
    const currentTrigger_0 = target_0?.closest<HTMLElement>('[role="tab"]');
    if (!currentTrigger_0 || !event_0.currentTarget.contains(currentTrigger_0)) return;
    event_0.preventDefault();
    event_0.stopPropagation();
    cycleDetailTab(event_0.shiftKey ? -1 : 1);
  }, [cycleDetailTab, detailTabValues.length]);
  const handleSectionKeyDown = React.useCallback((event_1: React.KeyboardEvent<HTMLElement>) => {
    if (event_1.key !== "Tab" || tabValues.length <= 1) return;
    if (tabsListRef.current?.contains(event_1.target as Node)) return;
    if (detailTabsListRef.current?.contains(event_1.target as Node)) return;
    event_1.preventDefault();
    cycleTab(event_1.shiftKey ? -1 : 1);
  }, [cycleTab, tabValues.length]);
  React.useEffect(() => {
    if (!currentMedicineId || tabValues.length === 0) return;
    const focusKey = `${currentMedicineId}:${activeTab ?? ""}`;
    if (lastAutoFocusedKeyRef.current === focusKey) return;
    lastAutoFocusedKeyRef.current = focusKey;
    const handle = window.requestAnimationFrame(() => {
      const activeTrigger_1 = tabsListRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      activeTrigger_1?.focus();
    });
    return () => window.cancelAnimationFrame(handle);
  }, [activeTab, currentMedicineId, tabValues.length]);
  const referentPriceSection = <ReferentPriceSection loading={referentQuery.isLoading} entries={referentQuery.data ?? []} />;
  return <section onKeyDown={handleSectionKeyDown} className="bg-card ring-foreground/8 flex flex-col gap-5 rounded-xl border-none p-5 shadow-xs ring-1">
      <header className={cn("grid gap-4 sm:items-start", details.vendor ? "sm:grid-cols-[auto_minmax(0,1fr)_minmax(220px,280px)]" : "sm:grid-cols-[auto_minmax(0,1fr)]")}>
        <MedicineImage imageSm={details.image_sm} imageBg={details.image_bg} alt={displayName} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <h2 className="text-foreground text-lg font-semibold capitalize">{displayName}</h2>
              <MedicineNameCopyButton value={displayName} label={t("cart.medicineName", {
              defaultValue: "Medicine name"
            })} />
            </div>
            {details.is_prescription ? <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="h-4 px-1 text-[9px] leading-none" title={t("medicines.badges.prescription")}>
                    {t("medicines.details.rxBadge")}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{t("medicines.badges.prescription")}</TooltipContent>
              </Tooltip> : null}
            {details.is_vital_essential ? <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" aria-label={t("medicines.details.vitalEssential")} title={t("medicines.details.vitalEssential")} className="flex size-6 rounded-full px-0 text-emerald-700 dark:text-emerald-300">
                    <BadgeCheck className="size-3.5" aria-hidden="true" />
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{t("medicines.details.vitalEssential")}</TooltipContent>
              </Tooltip> : null}
            {sourceName || sourceUrl ? <Tooltip>
                <TooltipTrigger asChild>
                  <Badge asChild={Boolean(sourceUrl)} variant="outline" aria-label={sourceAriaLabel} title={sourceTooltip} className="flex size-6 rounded-full px-0 text-sky-700 dark:text-sky-300">
                    {sourceUrl ? <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
                        <BookOpenText className="size-3.5" aria-hidden="true" />
                      </a> : <span>
                        <BookOpenText className="size-3.5" aria-hidden="true" />
                      </span>}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{sourceTooltip}</TooltipContent>
              </Tooltip> : null}
          </div>
          {internationalName ? <p className="text-muted-foreground mt-0.5 text-sm italic">{internationalName}</p> : null}
          {metaChips.length > 0 ? <div className="mt-3 flex flex-wrap gap-1.5">
              {metaChips.map(chip => <Badge key={chip.label} variant="secondary" className="h-6 rounded-full px-2 text-xs font-normal">
                  {chip.label}
                </Badge>)}
            </div> : null}
        </div>
        {details.vendor ? <div className="flex w-full flex-col gap-2 sm:max-w-70 sm:justify-self-end">
            <VendorSection vendor={details.vendor} />
            {referentPriceSection}
          </div> : null}
      </header>

      {details.vendor ? null : referentPriceSection}

      {tabItems.length > 0 ? <Tabs value={activeTab ?? undefined} onValueChange={handleTabChange} className="w-full">
          <div className="bg-muted/70 flex items-center rounded-xl">
            <TabsList ref={tabsListRef} className="scrollbar-hidden min-w-0 flex-1 justify-start overflow-x-auto bg-transparent" onKeyDown={handleTabsListKeyDown}>
              {tabItems.map(item_2 => <TabsTrigger key={item_2.value} value={item_2.value}>
                  {item_2.label}
                </TabsTrigger>)}
            </TabsList>
            <span aria-label={t("medicines.details.tabKeyboardHint", {
          defaultValue: "Tab switches medicine detail tabs"
        })} className="text-muted-foreground hidden h-9 shrink-0 items-center gap-1.5 px-3 font-mono text-[11px] leading-none sm:inline-flex">
              <Keyboard className="size-3.5" aria-hidden="true" />
              tab
            </span>
          </div>
          {tabItems.map(item_3 => {
        if (item_3.type === "details") {
          return <TabsContent key={item_3.value} value={item_3.value} className="scrollbar-hidden mt-3">
                  <Tabs value={activeDetailTab ?? undefined} onValueChange={handleDetailTabChange} className="w-full">
                    <TabsList ref={detailTabsListRef} className="scrollbar-hidden max-w-full min-w-0 justify-start overflow-x-auto" onKeyDown={handleDetailTabsListKeyDown}>
                      {detailTabItems.map(detailItem => <TabsTrigger key={detailItem.value} value={detailItem.value}>
                          {detailItem.label}
                        </TabsTrigger>)}
                    </TabsList>
                    {detailTabItems.map(detailItem_0 => <TabsContent key={detailItem_0.value} value={detailItem_0.value} className="scrollbar-hidden mt-3">
                        <DetailFieldList fields={detailItem_0.fields} />
                      </TabsContent>)}
                  </Tabs>
                </TabsContent>;
        }
        if (item_3.type === "icdCodes") {
          return <TabsContent key={item_3.value} value={item_3.value} className="scrollbar-hidden mt-3">
                  <IcdCodeList codes={icdCodes!} />
                </TabsContent>;
        }
        if (item_3.type === "variations") {
          return <TabsContent key={item_3.value} value={item_3.value} className="scrollbar-hidden mt-3">
                  {variationsError ? <p className="text-destructive text-sm">{variationsError}</p> : <VariationGroups items={sortedVariations} onSelect={onSelectVariation} />}
                </TabsContent>;
        }
        if (item_3.type === "certificates") {
          return <TabsContent key={item_3.value} value={item_3.value} className="scrollbar-hidden mt-3">
                  <MedicineCertificatesList items={certificates ?? []} loading={certificatesLoading} error={certificatesError} />
                </TabsContent>;
        }
        return <TabsContent key={item_3.value} value={item_3.value} className="scrollbar-hidden -mx-5 mt-3">
                <MedicineAnaloguesBrowser analogues={analogues ?? []} loading={analoguesLoading} error={analoguesError} resetKey={currentMedicineId} selectFirstOnLoad={false} onActivateMedicine={medicine => onSelectAnalogue?.(medicine.medicine_id)} />
              </TabsContent>;
      })}
        </Tabs> : null}
    </section>;
}
function DetailFieldList(t0) {
  const $ = _c(4);
  const {
    fields
  } = t0;
  let t1;
  if ($[0] !== fields) {
    t1 = fields.map(_temp);
    $[0] = fields;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="grid gap-3">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(field) {
  return <div key={field.key} className="bg-muted/20 rounded-lg border px-3 py-2.5"><dt className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{field.label}</dt><dd className="text-foreground/85 mt-1 text-sm leading-relaxed whitespace-pre-line">{normalizeText(field.value)}</dd></div>;
}
function MedicineNameCopyButton({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  const {
    t
  } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedFallback = `${label} copied`;
  const copyFallback = `Copy ${label}`;
  React.useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);
  const handleCopy = async () => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return;
    try {
      await navigator.clipboard.writeText(trimmedValue);
      const copiedMessage = t("common.copy.copiedLabeled", {
        label,
        defaultValue: copiedFallback
      });
      toast.success(copiedMessage.includes("{{label}}") ? copiedFallback : copiedMessage);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Silently ignore clipboard failures (e.g. permissions denied).
    }
  };
  const translatedAriaLabel = t("common.copy.copyLabeled", {
    label,
    defaultValue: copyFallback
  });
  const ariaLabel = translatedAriaLabel.includes("{{label}}") ? copyFallback : translatedAriaLabel;
  return <button type="button" onClick={handleCopy} aria-label={ariaLabel} title={ariaLabel} className="text-muted-foreground hover:text-foreground hover:bg-muted flex size-7 shrink-0 cursor-pointer items-center justify-center rounded transition-colors">
      {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
    </button>;
}
function IcdCodeList(t0) {
  const $ = _c(4);
  const {
    codes
  } = t0;
  let t1;
  if ($[0] !== codes) {
    t1 = codes.map(_temp2);
    $[0] = codes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="flex flex-col gap-2">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(item) {
  return <li key={item.code} className="flex items-start gap-3"><span className="bg-muted text-foreground shrink-0 rounded px-1.5 py-0.5 font-mono text-xs font-medium">{item.code}</span><div className="min-w-0"><span className="text-foreground text-sm">{item.description ?? item.code}</span>{item.parent_code ? <span className="text-muted-foreground ml-2 text-xs">{item.parent_code}</span> : null}</div></li>;
}
function PriceWithUpcoming(t0) {
  const $ = _c(25);
  const {
    current,
    upcoming,
    field,
    intlLocale,
    upcomingLabel
  } = t0;
  const currentValue = current ? current[field] : null;
  const upcomingValue = upcoming ? upcoming[field] : null;
  if (currentValue == null && upcomingValue == null) {
    return null;
  }
  const upcomingDate = upcoming?.enforceAt ?? null;
  if (currentValue == null && upcomingValue != null && upcomingDate) {
    let t1;
    if ($[0] !== upcomingValue) {
      t1 = formatUzsValue(upcomingValue);
      $[0] = upcomingValue;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span className="text-muted-foreground text-sm font-semibold italic tabular-nums">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== intlLocale || $[5] !== upcomingDate || $[6] !== upcomingLabel) {
      t3 = upcomingLabel(formatIssueDate(upcomingDate, intlLocale));
      $[4] = intlLocale;
      $[5] = upcomingDate;
      $[6] = upcomingLabel;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t3) {
      t4 = <span className="text-muted-foreground/80 text-[10px] tracking-wide uppercase">{t3}</span>;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t2 || $[11] !== t4) {
      t5 = <div className="flex flex-col leading-tight">{t2}{t4}</div>;
      $[10] = t2;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  let t1;
  if ($[13] !== currentValue) {
    t1 = formatUzsValue(currentValue);
    $[13] = currentValue;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== t1) {
    t2 = <span className="text-sm font-semibold tabular-nums">{t1}</span>;
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== intlLocale || $[18] !== upcomingDate || $[19] !== upcomingLabel || $[20] !== upcomingValue) {
    t3 = upcomingValue != null && upcomingDate ? <span className="text-muted-foreground/80 text-[11px] tabular-nums"><span aria-hidden={true}>→ </span>{formatUzsValue(upcomingValue)}<span className="ml-1 text-[10px] tracking-wide uppercase">{upcomingLabel(formatIssueDate(upcomingDate, intlLocale))}</span></span> : null;
    $[17] = intlLocale;
    $[18] = upcomingDate;
    $[19] = upcomingLabel;
    $[20] = upcomingValue;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== t2 || $[23] !== t3) {
    t4 = <div className="flex flex-col leading-tight">{t2}{t3}</div>;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  return t4;
}
function ReferentPriceSection(t0) {
  const $ = _c(46);
  const {
    loading,
    entries
  } = t0;
  const {
    t,
    i18n
  } = useTranslation();
  let t1;
  if ($[0] !== i18n.language) {
    t1 = toIntlLocale(i18n.language);
    $[0] = i18n.language;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const intlLocale = t1;
  if (loading) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <section className="border-border/60 bg-muted/20 rounded-lg border px-3 py-2"><Skeleton className="h-4 w-40" /></section>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let current;
  let retail;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let upcoming;
  let upcomingLabel;
  let wholesale;
  if ($[3] !== entries || $[4] !== t) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      ({
        current,
        upcoming
      } = splitReferentPrices(entries));
      wholesale = current?.wholesalePrice ?? null;
      retail = current?.retailPrice ?? null;
      if (wholesale == null && retail == null && upcoming == null) {
        t6 = null;
        break bb0;
      }
      const updatedAt = current?.updatedAt ?? null;
      const registrationNumber = current?.registrationNumber ?? upcoming?.registrationNumber ?? null;
      let t7;
      if ($[15] !== t) {
        t7 = date => t("wholesaleOffers.referentPriceUpcomingLabel", {
          date
        });
        $[15] = t;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      upcomingLabel = t7;
      t5 = "border-border/60 bg-muted/20 rounded-lg border px-3 py-2";
      t2 = "flex items-baseline justify-between gap-3";
      let t8;
      if ($[17] !== t) {
        t8 = t("medicines.referent.sectionTitle", {
          defaultValue: "Government referent prices"
        });
        $[17] = t;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      if ($[19] !== t8) {
        t3 = <p className="text-muted-foreground text-[11px] tracking-wide whitespace-nowrap uppercase">{t8}</p>;
        $[19] = t8;
        $[20] = t3;
      } else {
        t3 = $[20];
      }
      t4 = registrationNumber || updatedAt ? <p className="text-muted-foreground text-[11px] whitespace-nowrap">{registrationNumber ? t("medicines.referent.registrationNumber", {
          defaultValue: "Registration \u2116"
        }) : null}{registrationNumber ? " " : null}{registrationNumber}{registrationNumber && updatedAt ? " \xB7 " : null}{updatedAt ? t("medicines.referent.updated", {
          defaultValue: "Updated {{date}}",
          date: updatedAt
        }) : null}</p> : null;
    }
    $[3] = entries;
    $[4] = t;
    $[5] = current;
    $[6] = retail;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = upcoming;
    $[13] = upcomingLabel;
    $[14] = wholesale;
  } else {
    current = $[5];
    retail = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    upcoming = $[12];
    upcomingLabel = $[13];
    wholesale = $[14];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[21] !== t2 || $[22] !== t3 || $[23] !== t4) {
    t7 = <div className={t2}>{t3}{t4}</div>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== current || $[26] !== intlLocale || $[27] !== t || $[28] !== upcoming || $[29] !== upcomingLabel || $[30] !== wholesale) {
    t8 = wholesale != null || (upcoming?.wholesalePrice ?? null) != null ? <div><p className="text-muted-foreground text-[11px]">{t("medicines.referent.wholesalePrice", {
          defaultValue: "Wholesale"
        })}</p><PriceWithUpcoming current={current} upcoming={upcoming} field="wholesalePrice" intlLocale={intlLocale} upcomingLabel={upcomingLabel} /></div> : null;
    $[25] = current;
    $[26] = intlLocale;
    $[27] = t;
    $[28] = upcoming;
    $[29] = upcomingLabel;
    $[30] = wholesale;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== current || $[33] !== intlLocale || $[34] !== retail || $[35] !== t || $[36] !== upcoming || $[37] !== upcomingLabel) {
    t9 = retail != null || (upcoming?.retailPrice ?? null) != null ? <div><p className="text-muted-foreground text-[11px]">{t("medicines.referent.retailPrice", {
          defaultValue: "Retail"
        })}</p><PriceWithUpcoming current={current} upcoming={upcoming} field="retailPrice" intlLocale={intlLocale} upcomingLabel={upcomingLabel} /></div> : null;
    $[32] = current;
    $[33] = intlLocale;
    $[34] = retail;
    $[35] = t;
    $[36] = upcoming;
    $[37] = upcomingLabel;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== t8 || $[40] !== t9) {
    t10 = <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1">{t8}{t9}</div>;
    $[39] = t8;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== t10 || $[43] !== t5 || $[44] !== t7) {
    t11 = <section className={t5}>{t7}{t10}</section>;
    $[42] = t10;
    $[43] = t5;
    $[44] = t7;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
}
function VendorSection(t0) {
  const $ = _c(17);
  const {
    vendor,
    className
  } = t0;
  const [errored, setErrored] = React.useState(false);
  const src = !errored ? vendor.logo_big ?? vendor.logo_sm : null;
  const t1 = `/app/vendors?id=${vendor.id}`;
  let t2;
  if ($[0] !== className) {
    t2 = cn("bg-muted/30 hover:bg-muted/50 flex w-full items-center gap-3 rounded-lg border px-3 py-2 transition-colors sm:max-w-70", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== src || $[3] !== vendor.name) {
    t3 = src ? <Image src={src} alt={vendor.name} width={40} height={40} className="bg-card size-10 shrink-0 rounded-md object-contain" onError={() => setErrored(true)} /> : <div className="bg-card text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold">{vendor.name.slice(0, 2).toUpperCase()}</div>;
    $[2] = src;
    $[3] = vendor.name;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== vendor.name) {
    t4 = <p className="truncate text-sm font-medium">{vendor.name}</p>;
    $[5] = vendor.name;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== vendor.country) {
    t5 = vendor.country ? <span className="text-muted-foreground shrink-0 text-xs">{vendor.country}</span> : null;
    $[7] = vendor.country;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="min-w-0 flex-1 leading-tight">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t6) {
    t7 = <Link href={t1} className={t2}>{t3}{t6}</Link>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function isGenericMedicineProductType(value: string | null | undefined): boolean {
  return String(value ?? "").trim().toLowerCase() === "medicine";
}
function getVariationChipParts(item: MedicineVariation, options: {
  omitDosageForm?: boolean;
} = {}): string[] {
  const dosageForm = options.omitDosageForm ? null : translateDosageForm(item.dosage_form) ?? item.dosage_form;
  const strength = translateStrength(item.strength) ?? item.strength;
  const volume = item.volume ? String(item.volume) : null;
  const pack = item.pack != null ? `№${item.pack}` : null;
  const containerType = translateContainerType(item.container_type) ?? item.container_type;
  const productType = isGenericMedicineProductType(item.product_type) ? null : translateProductType(item.product_type) ?? item.product_type;
  return [dosageForm, strength, volume, pack, containerType, productType].filter((value): value is string => Boolean(value));
}
function getVariationGroupLabel(item: MedicineVariation, fallback: string): string {
  return (translateDosageForm(item.dosage_form) ?? item.dosage_form) || fallback;
}
function VariationGroups(t0) {
  const $ = _c(8);
  const {
    items,
    onSelect
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("medicines.details.variationsOther");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const otherLabel = t1;
  let t2;
  if ($[2] !== items || $[3] !== otherLabel) {
    const next = new Map();
    for (const item of items) {
      const key = item.dosage_form || "__other";
      const group = next.get(key);
      if (group) {
        group.items.push(item);
      } else {
        next.set(key, {
          label: getVariationGroupLabel(item, otherLabel),
          dosageForm: item.dosage_form,
          items: [item]
        });
      }
    }
    t2 = Array.from(next.values());
    $[2] = items;
    $[3] = otherLabel;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const groups = t2;
  let t3;
  if ($[5] !== groups || $[6] !== onSelect) {
    t3 = <div className="flex flex-col gap-2">{groups.map(group_0 => <div key={group_0.dosageForm ?? "__other"} className="flex flex-wrap items-start gap-x-2 gap-y-1.5"><div className="text-muted-foreground flex min-h-7 shrink-0 items-center gap-1 text-xs"><DosageFormIcon form={group_0.dosageForm} className="size-4 opacity-50" /><span>{group_0.label}:</span></div><ul className="flex min-w-0 flex-1 flex-wrap gap-1">{group_0.items.map(variation => <li key={variation.id} className="min-w-0"><VariationChip item={variation} onSelect={onSelect} /></li>)}</ul></div>)}</div>;
    $[5] = groups;
    $[6] = onSelect;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function VariationChip(t0) {
  const $ = _c(15);
  const {
    item,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== item) {
    const parts = getVariationChipParts(item, {
      omitDosageForm: true
    });
    const fallbackLabel = item.trade_name || String(item.id);
    t1 = parts.length > 0 ? parts.join(" \xB7 ") : fallbackLabel;
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  const vendorName = item.vendor?.name ?? null;
  const ariaLabel = vendorName ? `${label} · ${vendorName}` : label;
  let t2;
  if ($[2] !== item.id || $[3] !== onSelect) {
    t2 = () => onSelect?.(String(item.id));
    $[2] = item.id;
    $[3] = onSelect;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(filterToggleVariants({
      size: "xs"
    }), "h-auto max-w-full justify-start py-1 text-left");
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== label) {
    t4 = <span className="min-w-0 truncate">{label}</span>;
    $[6] = label;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== vendorName) {
    t5 = vendorName ? <span className="bg-muted text-muted-foreground max-w-28 truncate rounded px-1 text-[10px]">{vendorName}</span> : null;
    $[8] = vendorName;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== ariaLabel || $[11] !== t2 || $[12] !== t4 || $[13] !== t5) {
    t6 = <button type="button" aria-label={ariaLabel} onClick={t2} className={t3}>{t4}{t5}</button>;
    $[10] = ariaLabel;
    $[11] = t2;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function MedicineImage(t0) {
  const $ = _c(6);
  const {
    imageSm,
    imageBg,
    alt
  } = t0;
  const [errored, setErrored] = React.useState(false);
  const src = !errored ? imageBg ?? imageSm : null;
  if (!src) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="bg-muted/50 text-muted-foreground flex size-28 shrink-0 items-center justify-center rounded-lg"><Pill className="size-8" /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("size-28 shrink-0 rounded-lg object-contain", "bg-muted/30");
    t2 = () => setErrored(true);
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== alt || $[4] !== src) {
    t3 = <Image src={src} alt={alt} width={112} height={112} className={t1} onError={t2} />;
    $[3] = alt;
    $[4] = src;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}